/**
 * Leviathan Web - Email Logs Page
 * Quasar Prime LLC
 *
 * View sent email history with status tracking
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import {
  Mail,
  RefreshCw,
  Search,
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  Eye,
  X,
  Filter,
  Calendar,
} from 'lucide-react';
import { API_URL } from '@/config/api';

interface EmailLog {
  id: string;
  templateId: string | null;
  templateCode: string | null;
  to: string;
  cc: string | null;
  bcc: string | null;
  subject: string;
  htmlBody: string;
  textBody: string | null;
  status: 'PENDING' | 'SENT' | 'DELIVERED' | 'FAILED' | 'BOUNCED';
  error: string | null;
  sentAt: string | null;
  deliveredAt: string | null;
  openedAt: string | null;
  metadata: Record<string, unknown>;
  createdAt: string;
  template?: {
    name: string;
  };
  createdByUser?: {
    name: string;
  };
}

interface PaginatedResponse {
  data: EmailLog[];
  meta: {
    total: number;
    page: number;
    pageSize: number;
    totalPages: number;
  };
}

const STATUS_CONFIG = {
  PENDING: {
    label: 'Pending',
    icon: Clock,
    color: 'bg-slate-500/20 text-slate-400 border-slate-500/30',
  },
  SENT: {
    label: 'Sent',
    icon: CheckCircle,
    color: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  },
  DELIVERED: {
    label: 'Delivered',
    icon: CheckCircle,
    color: 'bg-green-500/20 text-green-400 border-green-500/30',
  },
  FAILED: {
    label: 'Failed',
    icon: XCircle,
    color: 'bg-red-500/20 text-red-400 border-red-500/30',
  },
  BOUNCED: {
    label: 'Bounced',
    icon: AlertTriangle,
    color: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  },
};

export default function EmailLogs() {
  const { accessToken } = useAuth();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [selectedLog, setSelectedLog] = useState<EmailLog | null>(null);

  // Fetch email logs
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['email-logs', page, search, statusFilter, dateRange],
    queryFn: async () => {
      const params = new URLSearchParams({
        page: page.toString(),
        pageSize: '25',
      });
      if (search) params.append('search', search);
      if (statusFilter) params.append('status', statusFilter);
      if (dateRange.start) params.append('startDate', dateRange.start);
      if (dateRange.end) params.append('endDate', dateRange.end);

      const response = await fetch(`${API_URL}/api/email/logs?${params}`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
      if (!response.ok) throw new Error('Failed to fetch email logs');
      return response.json() as Promise<PaginatedResponse>;
    },
    enabled: !!accessToken,
  });

  const logs = data?.data || [];
  const meta = data?.meta;

  return (
    <div className="min-h-screen bg-slate-900 p-6">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center bg-amber-500/20 border border-amber-500/30">
            <Mail className="h-5 w-5 text-amber-500" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-white">Email Logs</h1>
            <p className="text-sm text-slate-400">
              View sent email history and delivery status
            </p>
          </div>
        </div>

        <button
          onClick={() => refetch()}
          className="flex items-center gap-2 px-4 py-2 bg-slate-700 border border-slate-600 text-slate-300 hover:bg-slate-600 transition-colors"
        >
          <RefreshCw className="h-4 w-4" />
          Refresh
        </button>
      </div>

      {/* Filters */}
      <div className="mb-6 bg-slate-800/50 border border-slate-700 p-4">
        <div className="flex flex-wrap items-center gap-4">
          {/* Search */}
          <div className="flex-1 min-w-[250px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                placeholder="Search by recipient or subject..."
                className="w-full pl-10 pr-4 py-2 bg-slate-900 border border-slate-600 text-white placeholder-slate-500 focus:outline-none focus:border-amber-500"
              />
            </div>
          </div>

          {/* Status Filter */}
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-slate-400" />
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(1);
              }}
              className="px-3 py-2 bg-slate-900 border border-slate-600 text-white focus:outline-none focus:border-amber-500"
            >
              <option value="">All Status</option>
              <option value="PENDING">Pending</option>
              <option value="SENT">Sent</option>
              <option value="DELIVERED">Delivered</option>
              <option value="FAILED">Failed</option>
              <option value="BOUNCED">Bounced</option>
            </select>
          </div>

          {/* Date Range */}
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-slate-400" />
            <input
              type="date"
              value={dateRange.start}
              onChange={(e) => {
                setDateRange({ ...dateRange, start: e.target.value });
                setPage(1);
              }}
              className="px-3 py-2 bg-slate-900 border border-slate-600 text-white focus:outline-none focus:border-amber-500"
            />
            <span className="text-slate-500">to</span>
            <input
              type="date"
              value={dateRange.end}
              onChange={(e) => {
                setDateRange({ ...dateRange, end: e.target.value });
                setPage(1);
              }}
              className="px-3 py-2 bg-slate-900 border border-slate-600 text-white focus:outline-none focus:border-amber-500"
            />
          </div>
        </div>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="h-8 w-8 text-amber-500 animate-spin" />
        </div>
      )}

      {/* Empty State */}
      {!isLoading && logs.length === 0 && (
        <div className="bg-slate-800/50 border border-slate-700 p-12 text-center">
          <Mail className="h-12 w-12 text-slate-600 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-white mb-2">No Emails Found</h3>
          <p className="text-slate-400">
            {search || statusFilter || dateRange.start || dateRange.end
              ? 'No emails match your current filters.'
              : 'No emails have been sent yet.'}
          </p>
        </div>
      )}

      {/* Email Logs Table */}
      {!isLoading && logs.length > 0 && (
        <>
          <div className="bg-slate-800/50 border border-slate-700 overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-700 bg-slate-900/50">
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                    Recipient
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                    Subject
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                    Template
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                    Sent
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700">
                {logs.map((log) => {
                  const statusConfig = STATUS_CONFIG[log.status];
                  const StatusIcon = statusConfig.icon;
                  return (
                    <tr
                      key={log.id}
                      className="hover:bg-slate-800/50 transition-colors"
                    >
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex items-center gap-1.5 px-2 py-1 text-xs font-medium border ${statusConfig.color}`}
                        >
                          <StatusIcon className="h-3.5 w-3.5" />
                          {statusConfig.label}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-white text-sm">{log.to}</div>
                        {log.cc && (
                          <div className="text-xs text-slate-500">
                            CC: {log.cc}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-white text-sm truncate max-w-[300px]">
                          {log.subject}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-slate-400 text-sm">
                          {log.template?.name || log.templateCode || 'Custom'}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-slate-400 text-sm">
                          {log.sentAt
                            ? new Date(log.sentAt).toLocaleString()
                            : new Date(log.createdAt).toLocaleString()}
                        </div>
                        {log.createdByUser && (
                          <div className="text-xs text-slate-500">
                            by {log.createdByUser.name}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => setSelectedLog(log)}
                          className="p-2 text-slate-400 hover:text-amber-500 hover:bg-slate-700 transition-colors"
                          title="View Details"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {meta && meta.totalPages > 1 && (
            <div className="mt-4 flex items-center justify-between">
              <div className="text-sm text-slate-400">
                Showing {(meta.page - 1) * meta.pageSize + 1} to{' '}
                {Math.min(meta.page * meta.pageSize, meta.total)} of {meta.total}{' '}
                emails
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-3 py-1 bg-slate-700 text-slate-300 hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Previous
                </button>
                <span className="text-slate-400">
                  Page {meta.page} of {meta.totalPages}
                </span>
                <button
                  onClick={() => setPage((p) => Math.min(meta.totalPages, p + 1))}
                  disabled={page === meta.totalPages}
                  className="px-3 py-1 bg-slate-700 text-slate-300 hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Email Detail Modal */}
      {selectedLog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 border border-slate-700 max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="p-4 border-b border-slate-700 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-white">Email Details</h3>
                <p className="text-sm text-slate-400">{selectedLog.subject}</p>
              </div>
              <button
                onClick={() => setSelectedLog(null)}
                className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {/* Status & Timestamps */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <div className="text-xs text-slate-500 mb-1">Status</div>
                  <span
                    className={`inline-flex items-center gap-1.5 px-2 py-1 text-xs font-medium border ${STATUS_CONFIG[selectedLog.status].color}`}
                  >
                    {STATUS_CONFIG[selectedLog.status].label}
                  </span>
                </div>
                <div>
                  <div className="text-xs text-slate-500 mb-1">Created</div>
                  <div className="text-sm text-white">
                    {new Date(selectedLog.createdAt).toLocaleString()}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-slate-500 mb-1">Sent</div>
                  <div className="text-sm text-white">
                    {selectedLog.sentAt
                      ? new Date(selectedLog.sentAt).toLocaleString()
                      : '-'}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-slate-500 mb-1">Delivered</div>
                  <div className="text-sm text-white">
                    {selectedLog.deliveredAt
                      ? new Date(selectedLog.deliveredAt).toLocaleString()
                      : '-'}
                  </div>
                </div>
              </div>

              {/* Error Message */}
              {selectedLog.error && (
                <div className="p-4 bg-red-500/10 border border-red-500/30">
                  <div className="flex items-start gap-2">
                    <XCircle className="h-5 w-5 text-red-400 flex-shrink-0" />
                    <div>
                      <div className="text-red-400 font-medium">Error</div>
                      <p className="text-sm text-slate-300 mt-1">
                        {selectedLog.error}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Recipients */}
              <div className="bg-slate-900/50 border border-slate-700 p-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <div className="text-xs text-slate-500 mb-1">To</div>
                    <div className="text-sm text-white">{selectedLog.to}</div>
                  </div>
                  {selectedLog.cc && (
                    <div>
                      <div className="text-xs text-slate-500 mb-1">CC</div>
                      <div className="text-sm text-white">{selectedLog.cc}</div>
                    </div>
                  )}
                  {selectedLog.bcc && (
                    <div>
                      <div className="text-xs text-slate-500 mb-1">BCC</div>
                      <div className="text-sm text-white">{selectedLog.bcc}</div>
                    </div>
                  )}
                </div>
              </div>

              {/* Template Info */}
              {(selectedLog.template || selectedLog.templateCode) && (
                <div className="bg-slate-900/50 border border-slate-700 p-4">
                  <div className="text-xs text-slate-500 mb-1">Template</div>
                  <div className="text-sm text-white">
                    {selectedLog.template?.name || selectedLog.templateCode}
                  </div>
                </div>
              )}

              {/* HTML Preview */}
              <div className="border border-slate-700">
                <div className="px-4 py-2 bg-slate-900/50 border-b border-slate-700">
                  <div className="text-xs text-slate-500">HTML Content</div>
                </div>
                <div className="bg-white p-4 max-h-[400px] overflow-y-auto">
                  <div
                    dangerouslySetInnerHTML={{ __html: selectedLog.htmlBody }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
